import {
  FilamentPriceList,
  InventorySummary,
  PrinterSlotList,
  ShoppingList,
  SpoolDetail,
  SpoolList,
  type SpoolStatus,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export const spoolsKey = ['spools'] as const;
export const inventoryKey = ['inventory'] as const;

export function useSpools(
  f: { status?: SpoolStatus | ''; filamentId?: string; active?: boolean } = {},
) {
  const params = new URLSearchParams({ limit: '200' });
  if (f.status) params.set('status', f.status);
  if (f.filamentId) params.set('filamentId', f.filamentId);
  if (f.active !== undefined) params.set('active', String(f.active));
  return useQuery({
    queryKey: [...spoolsKey, 'list', params.toString()],
    queryFn: () => api.get(`/spools?${params.toString()}`, SpoolList),
  });
}

export function useSpool(id: string | undefined) {
  return useQuery({
    queryKey: [...spoolsKey, id],
    queryFn: () => api.get(`/spools/${id}`, SpoolDetail),
    enabled: !!id,
  });
}

export const useInventorySummary = () =>
  useQuery({
    queryKey: [...inventoryKey, 'summary'],
    queryFn: () => api.get('/inventory/summary', InventorySummary),
  });

export const useShoppingList = () =>
  useQuery({
    queryKey: [...inventoryKey, 'shopping-list'],
    queryFn: () => api.get('/inventory/shopping-list', ShoppingList),
  });

/** Sets how many to buy (null = back to the suggestion); the list comes back updated. */
export function useSetShopping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { kind: 'filaments' | 'supplies'; id: string; quantity: number | null }) =>
      api.put(`/inventory/shopping-list/${v.kind}/${v.id}`, ShoppingList, {
        quantity: v.quantity,
      }),
    onSuccess: (list) => qc.setQueryData([...inventoryKey, 'shopping-list'], list),
  });
}

export const useFilamentPrices = (id: string) =>
  useQuery({
    queryKey: ['filaments', id, 'prices'],
    queryFn: () => api.get(`/filaments/${id}/prices`, FilamentPriceList),
  });

export const usePrinterSlots = (printerId: string | undefined) =>
  useQuery({
    queryKey: ['printers', printerId, 'slots'],
    queryFn: () => api.get(`/printers/${printerId}/slots`, PrinterSlotList),
    enabled: !!printerId,
  });

/** Spools, filament stock, supplies, slots and the summaries all move together. */
export function useInvalidateInventory() {
  const qc = useQueryClient();
  return () => {
    for (const key of [
      spoolsKey,
      inventoryKey,
      ['filaments'],
      ['supplies'],
      ['packaging-kits'],
      ['printers'],
    ]) {
      void qc.invalidateQueries({ queryKey: key });
    }
  };
}

/** "1.250 g" */
export const formatG = (g: number) => `${Math.round(g).toLocaleString('pt-BR')} g`;
